import { useEffect, useState } from 'react';
import './game-view.styles.scss';
import { Link } from 'react-router-dom';
import { listenToDoc } from '@/utils/getDataFromDb';
import TeamBadge from '@/components/SHARED/team-badge/team-badge.component';
import TeamName from '@/components/SHARED/team-name/team-name.component';
import BoxScore from '@/components/SHARED/box-score/box-score.component';
import PlayByPlay from '@/components/SHARED/play-by-play/play-by-play.component';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';
import ResultBanner from '@/components/SHARED/result-banner/result-banner.component';
import { Top25Context } from '@/utils/top25';
import { DEFENSES, DEPTHS, OFFENSES, PACES, strategyLabel } from '@shared/strategy.js';
import { matchupStory } from '@shared/matchupStory.js';
import StrategyReview from '@/components/SHARED/strategy-review/strategy-review.component';

// One finished game: score, box scores and play-by-play.
// playerLink(teamId, playerId): optional, links names in the box scores.
// teamId: the manager's team, for a Win/Loss banner when it played.
// league, teamStats (summary/teamStats): for the strategy review's season
// comparisons; without them it shows the game's numbers only.
const GameView = ({ leagueId, gameId, periodsInRegulation, teamLink, playerLink, teamId, league, teamStats }) => {

    const [ game, setGame ] = useState(undefined);
    const [ tab, setTab ] = useState('box');

    useEffect(() => {
        let listener;
        listenToDoc({
            'path': `leagues/${leagueId}/games/${gameId}`,
            'callback': setGame,
            'handleListener': (unsub) => { listener = unsub; },
        });
        return () => { if (listener) listener(); }
    }, [leagueId, gameId])

    if (game === undefined) return <div className='center-col'><ThreeDotsLoader /></div>;
    if (game === null) return <p className='meta'>This game hasn&apos;t been played yet.</p>;

    const side = (s) => (
        <Link to={teamLink(s.id)} className={`final-team ${game.winner === s.id ? 'won' : ''}`}>
            <TeamBadge team={s} size='lg' />
            <span className='final-name'><TeamName team={s} /></span>
            <span className='final-score'>{s.score}</span>
        </Link>
    );

    // A past season's game (archived, so no play-by-play): today's Top 25
    // would be misleading next to its teams.
    const content = (
        <div className='game-view'>
            <ResultBanner game={game} teamId={teamId} />
            <div className='g-card final-card'>
                {side(game.away)}
                <div className='final-label'>
                    Final{game.overtimes ? ` (${game.overtimes === 1 ? 'OT' : `${game.overtimes}OT`})` : ''}
                    <span className='meta'>Day {game.day}{game.conf ? ' · Conference' : ''}{game.neutral ? ' · Neutral site' : ''}</span>
                    {game.label && <span className='meta'>{game.label}</span>}
                    {game.playedAt && <span className='meta game-played-at'>Played {playedAtText(game.playedAt)}</span>}
                    {game.rivalry && <span className='game-rivalry'>{game.rivalry.name ?? 'Rivalry game'}</span>}
                    {[game.away, game.home].filter(s => s.payback).map(s => (
                        <span key={s.id} className='game-payback'>Payback game for {s.abbr ?? s.name}</span>
                    ))}
                </div>
                {side(game.home)}
            </div>

            {(game.home.strategy || game.away.strategy) &&
                <div className='g-card game-strategies'>
                    {[game.away, game.home].filter(s => s.strategy).map(s => (
                        <div key={s.id}>
                            <b>{s.abbr}</b>{' '}
                            {strategyLabel(OFFENSES, s.strategy.offense)} · {strategyLabel(DEFENSES, s.strategy.defense)} ·{' '}
                            {strategyLabel(PACES, s.strategy.pace)} pace · {strategyLabel(DEPTHS, s.strategy.depth)}
                            {s.strategy.keyOn && <> · Keyed on {keyedName(game, s)}</>}
                        </div>
                    ))}
                    {matchupStory(game) &&
                        <div className='game-matchup'>
                            <b>Key matchup:</b> {matchupStory(game).headline}. <span className='meta'>{matchupStory(game).stat}</span>
                        </div>
                    }
                    <StrategyReview
                        game={game}
                        teamStats={teamStats}
                        seasonYear={league?.season?.year ?? 1}
                        type={league?.settings?.type}
                        minutesPerGame={league?.settings?.minutesPerGame}
                        teamId={teamId} />
                </div>
            }

            {/* Past seasons keep the box score but not the play-by-play. */}
            {game.pbp ?
                <div className='game-tabs' role='tablist'>
                    <button role='tab' aria-selected={tab === 'box'} className={`chip ${tab === 'box' ? 'active' : ''}`} onClick={() => setTab('box')}>Box score</button>
                    <button role='tab' aria-selected={tab === 'pbp'} className={`chip ${tab === 'pbp' ? 'active' : ''}`} onClick={() => setTab('pbp')}>Play-by-play</button>
                </div> :
                <p className='meta'>Season {game.year} box score. Play-by-play isn&apos;t kept for past seasons.</p>
            }

            {(tab === 'box' || !game.pbp) &&
                <div className='g-card'>
                    <BoxScore teamResult={game.away} players={game.away.roster} playerLink={playerLink} />
                    <div className='g-space-1'></div>
                    <BoxScore teamResult={game.home} players={game.home.roster} playerLink={playerLink} />
                    {game.injuries?.length > 0 &&
                        <p className='meta'>
                            Injuries: {game.injuries.map(i => {
                                const s = game.home.id === i.teamId ? game.home : game.away;
                                const p = s.roster[i.playerId];
                                return `${p ? `${p.fName} ${p.lName}` : 'Unknown'} (${s.abbr}, out ${i.gamesOut})`;
                            }).join(', ')}
                        </p>
                    }
                </div>
            }
            {tab === 'pbp' && game.pbp &&
                <PlayByPlay
                    pbp={game.pbp}
                    homeName={game.home.abbr}
                    awayName={game.away.abbr}
                    periodsInRegulation={periodsInRegulation} />
            }
        </div>
    );
    return game.pbp ? content : <Top25Context.Provider value={{}}>{content}</Top25Context.Provider>;
}

// When the game was simulated, in the viewer's time zone, e.g.
// "Tue, Nov 3, 2026, 8:00 PM".
// The player a side's defense keyed on: on the other side's roster.
function keyedName(game, side) {
    const other = side.id === game.home.id ? game.away : game.home;
    const p = other.roster?.[side.strategy.keyOn];
    return p ? `${p.fName} ${p.lName}` : 'a player';
}

function playedAtText(ms) {
    try {
        return new Date(ms).toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
    } catch {
        return new Date(ms).toLocaleString();
    }
}

export default GameView
